import { useEffect, useState } from "react";
import { getImageUrl } from "../../utils/imageUtils";

/**
 * Memorial hero for the login page (TR-10 report 4). A calm, elegant panel with an
 * emotional headline + subtext and carousel dots. Each slide is one of two kinds
 * (TR-22):
 *
 *  - PRODUCT slide — a gently floating keepsake card (image + caption) over the
 *    parent panel's gradient, clickable through to that product.
 *  - BANNER slide — `banner_image_url` fills the whole panel edge to edge, with a
 *    scrim so the headline stays readable. No card, nothing to click.
 *
 * `show_product === false` (or a slide with no product) selects the banner
 * treatment. The caption is optional on both.
 */
export default function InfinitySlider({ slides = [], theme = {}, onPick, headline, subtext }) {
  const [i, setI] = useState(0);
  const has = slides.length > 0;
  const accent = theme.accent_color || "#E0A93C";

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setI((n) => (n + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [slides.length]);

  // A stale index would read past the end after slides shrink (admin preview).
  useEffect(() => {
    setI((n) => (n < slides.length ? n : 0));
  }, [slides.length]);

  const s = has ? slides[i] : null;
  // Banner treatment needs artwork AND the product card switched off — a slide
  // that still features a product keeps the card even if a banner was uploaded.
  const showsProduct = s ? s.show_product !== false : false;
  const hasBanners = slides.some((sl) => sl.show_product === false && sl.banner_image_url);

  return (
    <div className="relative w-full h-full overflow-hidden flex flex-col justify-between p-8 lg:p-12 text-white">
      {/* Full-bleed banner artwork crossfading (fade in & fade out simultaneously) */}
      {slides.map((slide, idx) => {
        const isCurrent = idx === i;
        const isBanner = slide.show_product === false && slide.banner_image_url;
        if (!isBanner) return null;
        return (
          <img
            key={slide._id || slide.banner_image_url || `banner-${idx}`}
            src={getImageUrl(slide.banner_image_url)}
            alt={slide.text || ""}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out ${
              isCurrent ? "opacity-100 scale-100" : "opacity-0 scale-[1.03] pointer-events-none"
            }`}
          />
        );
      })}

      {/* Subtle bottom gradient to ensure text readability without obscuring artwork */}
      {hasBanners ? (
        <div className="absolute inset-x-0 bottom-0 h-[28%] bg-gradient-to-t from-black/75 via-black/25 to-transparent pointer-events-none z-[1]" />
      ) : (
        <>
          {/* Soft decorative depth (gradient panel only) */}
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{ background: "#fff", filter: "blur(60px)" }} />
          <div className="absolute bottom-16 -left-10 w-44 h-44 rounded-full opacity-10 pointer-events-none" style={{ background: "#fff", filter: "blur(40px)" }} />
        </>
      )}

      <div className="relative z-10 text-white/90 text-sm font-semibold tracking-wide drop-shadow-sm">Infinity Touch</div>

      {/* Floating keepsake card — product slides only */}
      <div className="relative z-10 flex-1 flex items-center justify-center py-6 lg:max-w-xl">
        {s && showsProduct ? (
          <button
            onClick={() => s.product_id && onPick?.(s.product_id)}
            style={{ cursor: s.product_id && onPick ? "pointer" : "default" }}
            className="relative"
          >
            <div key={i} className="bg-white rounded-3xl shadow-2xl p-5 w-52 sm:w-60 animate-in fade-in zoom-in-95 duration-700"
              style={{ animation: "itfloat 5s ease-in-out infinite" }}>
              <div className="aspect-square rounded-2xl bg-slate-50 overflow-hidden flex items-center justify-center">
                {s.background_image_url ? (
                  <img src={getImageUrl(s.background_image_url)} alt={s.text || ""} className="w-full h-full object-contain" />
                ) : null}
              </div>
              {s.text ? <p className="mt-3 text-sm font-semibold text-slate-800 text-center line-clamp-1">{s.text}</p> : null}
            </div>
          </button>
        ) : null}
      </div>

      {/* Headline + subtext + dots */}
      <div className="relative z-10 lg:max-w-xl">
        <h2
          key={`headline-${i}`}
          className="text-3xl lg:text-4xl font-bold leading-tight drop-shadow-md animate-in fade-in duration-500"
        >
          {s?.text || headline || theme.hero_headline || "Memories beyond a lifetime"}
        </h2>
        <p className="mt-2 text-white/90 text-sm lg:text-base max-w-md drop-shadow">
          {subtext || "Cherish your loved one with a keepsake made from their memorial print."}
        </p>
        {slides.length > 1 ? (
          <div className="mt-5 flex gap-2">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
                className="h-2 rounded-full transition-all shadow-sm duration-500"
                style={{ width: idx === i ? 26 : 8, backgroundColor: idx === i ? accent : "rgba(255,255,255,0.7)" }} />
            ))}
          </div>
        ) : null}
      </div>

      <style>{`@keyframes itfloat {0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}`}</style>
    </div>
  );
}
